import type { Conversation, ConversationKeyEnvelope, E2eeKeyBackup, E2eeMyKeys, E2eePublicKeys, PublicJwk } from "@atrium/shared";
import { api } from "../api";
import {
  fingerprint,
  generateIdentity,
  generateRecoveryCode,
  newConversationKey,
  openBackup,
  openPasswordBackup,
  sameKey,
  sealBackup,
  sealPasswordBackup,
  unwrapConversationKey,
  wrapConversationKey,
  type Identity,
} from "./crypto";
import { loadIdentity, loadPins, saveIdentity, savePins, wipeKeys, type Pin } from "./store";

/*
 * This device's end-to-end encryption session: whose keys are loaded, who we
 * trust, and the conversation keys we've unwrapped. Framework-free; React
 * subscribes through `subscribe` / `snapshot`.
 */

export type E2eeStatus = "loading" | "off" | "locked" | "ready" | "unavailable";

interface State {
  userId: string | null;
  status: E2eeStatus;
  server: E2eeMyKeys | null;
}

let state: State = { userId: null, status: "loading", server: null };
let identity: Identity | null = null;
const listeners = new Set<() => void>();

const set = (patch: Partial<State>) => {
  state = { ...state, ...patch };
  for (const l of listeners) l();
};
export const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export const snapshot = () => state;

/** Thrown when sending would trust a key that changed since we last saw it. */
export class KeyChangedError extends Error {
  constructor(public userIds: string[]) {
    super("Someone's security key changed. Check it before sending.");
  }
}

/** Resolves once the session has finished loading (any status but "loading"). */
export function settled(): Promise<void> {
  if (state.status !== "loading") return Promise.resolve();
  return new Promise((resolve) => {
    const off = subscribe(() => {
      if (state.status !== "loading") {
        off();
        resolve();
      }
    });
  });
}

const supported = () => typeof indexedDB !== "undefined" && !!globalThis.crypto?.subtle;

/**
 * The password just typed to sign in or sign up, held in memory only until
 * the session has used it: to create keys on first sign-in, to open the
 * password-sealed backup on a new device, or (if that fails) to re-seal it
 * once the recovery code unlocks this device. Never stored.
 */
let signInPassword: string | null = null;
let signInPasswordTimer: ReturnType<typeof setTimeout> | undefined;

/** Call with the password right before a successful sign-in or sign-up is applied. */
export function rememberSignInPassword(password: string) {
  signInPassword = password;
  clearTimeout(signInPasswordTimer);
  signInPasswordTimer = setTimeout(forgetSignInPassword, 15 * 60_000);
}

function forgetSignInPassword() {
  signInPassword = null;
  clearTimeout(signInPasswordTimer);
}

/**
 * Load this account's keys from the server and this device's keys from
 * IndexedDB. Encryption is on by default: right after signing in, an account
 * without keys gets them, and a new device unlocks with the password.
 */
export async function initSession(userId: string) {
  if (!supported()) return set({ userId, status: "unavailable", server: null });
  if (state.userId !== userId) {
    identity = null;
    convKeys.clear();
    publicCache.clear();
    pins = null;
  }
  set({ userId });
  const [{ keys }, local] = await Promise.all([api.get<{ keys: E2eeMyKeys | null }>("/api/e2ee/keys/me"), loadIdentity(userId).catch(() => undefined)]);
  const password = signInPassword;
  if (!keys) {
    identity = null;
    set({ status: "off", server: null });
    if (password) {
      forgetSignInPassword();
      // Nobody sees this recovery code; Settings offers to make one they can keep.
      await publish(false, password, false).catch((e) => console.warn("Couldn't set up encrypted messages", e));
    }
    return;
  }
  // The account's keys were reset elsewhere: this device's copy is stale.
  if (local && local.fingerprint === keys.fingerprint && sameKey(local.encryptionKey, keys.encryptionKey) && sameKey(local.signingKey, keys.signingKey)) {
    identity = local;
    forgetSignInPassword();
    return set({ status: "ready", server: keys });
  }
  identity = null;
  set({ status: "locked", server: keys });
  if (password && keys.passwordBackup) {
    try {
      await restore(await openPasswordBackup(keys.passwordBackup, password, userId, keys), keys);
      forgetSignInPassword();
    } catch {
      /* Changed since it was sealed (a reset): the recovery code unlocks, then this password re-seals it. */
    }
  }
}

async function restore({ identity: restored }: { identity: Identity }, keys: E2eeMyKeys) {
  if (!state.userId) throw new Error("Not signed in");
  await saveIdentity(state.userId, restored);
  identity = restored;
  set({ status: "ready", server: keys });
  await pinSelf(restored);
}

/** Forget everything in memory and this device's private keys (sign out). */
export async function endSession() {
  forgetSignInPassword();
  identity = null;
  convKeys.clear();
  publicCache.clear();
  pins = null;
  set({ userId: null, status: "loading", server: null });
  await wipeKeys().catch(() => {});
}

function requireReady(): { userId: string; identity: Identity } {
  if (!state.userId || !identity || state.status !== "ready") throw new Error("Unlock encrypted messages on this device first");
  return { userId: state.userId, identity };
}

async function publish(replace: boolean, password: string, showCode = true): Promise<string> {
  const userId = state.userId;
  if (!userId) throw new Error("Not signed in");
  const { identity: fresh, privateJwks } = await generateIdentity();
  const code = generateRecoveryCode();
  const [backup, passwordBackup] = await Promise.all([sealBackup(privateJwks, code, userId), sealPasswordBackup(privateJwks, password, userId)]);
  const { keys } = await api.put<{ keys: E2eeMyKeys }>("/api/e2ee/keys", {
    encryptionKey: fresh.encryptionKey,
    signingKey: fresh.signingKey,
    backup,
    passwordBackup,
    recoveryConfirmed: showCode,
    replace,
    password,
  });
  await saveIdentity(userId, fresh);
  identity = fresh;
  convKeys.clear();
  set({ status: "ready", server: keys });
  await pinSelf(fresh);
  return code;
}

/** First-time setup. Returns the recovery code: shown once, never stored by us. */
export const setUp = (password: string) => publish(false, password);

/** New keys. Messages encrypted to the old keys can no longer be read by this account. */
export const reset = (password: string) => publish(true, password);

async function freshKeys() {
  const { keys } = await api.get<{ keys: E2eeMyKeys | null }>("/api/e2ee/keys/me");
  if (!keys) throw new Error("Encrypted messages aren't set up on this account");
  return keys;
}

/** Restore this account's keys onto this device from the recovery-code backup. */
export async function unlock(code: string) {
  const userId = state.userId;
  if (!userId) throw new Error("Not signed in");
  let keys = await freshKeys();
  const opened = await openBackup(keys.backup, code, userId, keys);
  // Signed in with a password the backup wasn't sealed under (it was reset): seal it under this one.
  const password = signInPassword;
  forgetSignInPassword();
  if (password) {
    const passwordBackup = await sealPasswordBackup(opened.privateJwks, password, userId);
    if (await api.put("/api/e2ee/keys/password-backup", { backup: passwordBackup, password }).then(() => true, () => false)) keys = { ...keys, passwordBackup };
  }
  await restore(opened, keys);
}

/** Restore this account's keys onto this device with the account password. */
export async function unlockWithPassword(password: string) {
  const userId = state.userId;
  if (!userId) throw new Error("Not signed in");
  const keys = await freshKeys();
  if (!keys.passwordBackup) throw new Error("Use your recovery code: your password can't unlock encrypted messages yet");
  await restore(await openPasswordBackup(keys.passwordBackup, password, userId, keys), keys);
}

/**
 * Keys sealed under an account password, for this device to open. Prefers the
 * password copy; the recovery code works too (for accounts set up before it existed).
 */
async function openWith(secret: { password: string } | { code: string }) {
  const userId = state.userId;
  if (!userId || !state.server) throw new Error("Encrypted messages aren't set up");
  if ("code" in secret) return openBackup(state.server.backup, secret.code, userId, state.server);
  if (!state.server.passwordBackup) throw new Error("Enter your recovery code instead");
  return openPasswordBackup(state.server.passwordBackup, secret.password, userId, state.server);
}

/** A new recovery code, for accounts whose keys were made automatically (nobody has seen theirs). */
export async function newRecoveryCode(password: string): Promise<string> {
  const userId = state.userId!;
  const { privateJwks } = await openWith({ password });
  const code = generateRecoveryCode();
  const backup = await sealBackup(privateJwks, code, userId);
  await api.put("/api/e2ee/keys/backup", { backup, password });
  set({ server: { ...state.server!, backup, recoveryConfirmed: true } });
  return code;
}

/** Let the account password unlock encrypted messages on other devices (accounts set up before that was the default). */
export async function enablePasswordUnlock(code: string, password: string) {
  const userId = state.userId!;
  const { privateJwks } = await openWith({ code });
  const backup = await sealPasswordBackup(privateJwks, password, userId);
  await api.put("/api/e2ee/keys/password-backup", { backup, password });
  set({ server: { ...state.server!, passwordBackup: backup } });
}

/**
 * Before changing the account password: the keys sealed under the new one,
 * opened with the current one. Null if the password can't unlock them now.
 * The server drops the old copy when the password changes; save this after.
 */
export async function sealForNewPassword(current: string, next: string) {
  if (!state.userId || !state.server?.passwordBackup) return null;
  try {
    const { privateJwks } = await openWith({ password: current });
    return await sealPasswordBackup(privateJwks, next, state.userId);
  } catch {
    return null;
  }
}

export async function savePasswordBackup(backup: E2eeKeyBackup, password: string) {
  await api.put("/api/e2ee/keys/password-backup", { backup, password });
  if (state.server) set({ server: { ...state.server, passwordBackup: backup } });
}

/** Re-seal the backup under a new recovery code. Needs the current one (private keys never leave this device in the clear) and the account password. */
export async function changeRecoveryCode(current: string, password: string): Promise<string> {
  const userId = state.userId;
  if (!userId || !state.server) throw new Error("Encrypted messages aren't set up");
  const { privateJwks } = await openBackup(state.server.backup, current, userId, state.server);
  const code = generateRecoveryCode();
  const backup = await sealBackup(privateJwks, code, userId);
  await api.put("/api/e2ee/keys/backup", { backup, password });
  set({ server: { ...state.server, backup, recoveryConfirmed: true } });
  return code;
}

/** Set up from inside a conversation: like signing in, no recovery code yet (Settings offers one). */
export const setUpQuietly = (password: string) => publish(false, password, false).then(() => {});

/* ───── Other people's keys, and trust ───── */

const publicCache = new Map<string, E2eePublicKeys & { computed: string }>();
let pins: Record<string, Pin> | null = null;

async function getPins() {
  if (!state.userId) return {};
  return (pins ??= await loadPins(state.userId));
}

async function writePins(next: Record<string, Pin>) {
  pins = next;
  if (state.userId) await savePins(state.userId, next);
  set({});
}

async function pinSelf(me: Identity) {
  if (!state.userId) return;
  const p = await getPins();
  await writePins({ ...p, [state.userId]: { fingerprint: me.fingerprint, verified: true, signingKeys: keepUnique([...(p[state.userId]?.signingKeys ?? []), me.signingKey]) } });
}

const keepUnique = (keys: PublicJwk[]) => keys.filter((k, i) => keys.findIndex((o) => sameKey(o, k)) === i).slice(-10);

/** Public keys for people, with fingerprints computed here rather than taken from the server. */
export async function publicKeys(userIds: string[], fresh = false): Promise<Map<string, E2eePublicKeys & { computed: string }>> {
  const want = [...new Set(userIds)].filter((u) => fresh || !publicCache.has(u));
  for (let i = 0; i < want.length; i += 50) {
    const { items } = await api.get<{ items: E2eePublicKeys[] }>(`/api/e2ee/keys?userIds=${want.slice(i, i + 50).join(",")}`);
    const got = new Set(items.map((k) => k.userId));
    for (const id of want.slice(i, i + 50)) if (!got.has(id)) publicCache.delete(id);
    for (const k of items) publicCache.set(k.userId, { ...k, computed: await fingerprint(k.encryptionKey, k.signingKey) });
  }
  return new Map(userIds.filter((u) => publicCache.has(u)).map((u) => [u, publicCache.get(u)!]));
}

export type Trust = "new" | "trusted" | "verified" | "changed";

/**
 * Trust on first use: the first key we see for someone is pinned. A different
 * key later is "changed" until the user accepts it, so a server that swaps in
 * its own key can't silently read along.
 */
export async function trustOf(userId: string, keys?: E2eePublicKeys & { computed: string }): Promise<Trust> {
  const k = keys ?? (await publicKeys([userId])).get(userId);
  if (!k) return "new";
  const pin = (await getPins())[userId];
  if (!pin) {
    const p = await getPins();
    await writePins({ ...p, [userId]: { fingerprint: k.computed, verified: false, signingKeys: [k.signingKey] } });
    return "trusted";
  }
  if (pin.fingerprint !== k.computed) return "changed";
  return pin.verified ? "verified" : "trusted";
}

/** Accept someone's new key (after a reset). Their old signing keys stay trusted for their old messages. */
export async function acceptKey(userId: string, verified = false) {
  const k = (await publicKeys([userId], true)).get(userId);
  if (!k) return;
  const p = await getPins();
  await writePins({ ...p, [userId]: { fingerprint: k.computed, verified, signingKeys: keepUnique([...(p[userId]?.signingKeys ?? []), k.signingKey]) } });
}

export async function setVerified(userId: string, verified: boolean) {
  const p = await getPins();
  if (p[userId]) await writePins({ ...p, [userId]: { ...p[userId], verified } });
}

/** Signing keys we trust for someone, newest last (old ones verify their older messages). */
export async function trustedSigningKeys(userId: string): Promise<PublicJwk[]> {
  const k = (await publicKeys([userId])).get(userId);
  const trust = await trustOf(userId, k);
  const pinned = (await getPins())[userId]?.signingKeys ?? [];
  return trust === "changed" ? pinned : keepUnique([...pinned, ...(k ? [k.signingKey] : [])]);
}

export async function myFingerprint() {
  return identity?.fingerprint ?? state.server?.fingerprint ?? null;
}

/* ───── Conversation keys ───── */

const convKeys = new Map<string, Map<number, CryptoKey>>();
const loading = new Map<string, Promise<void>>();

async function loadConversationKeys(conversationId: string) {
  const { userId, identity: me } = requireReady();
  const { items } = await api.get<{ items: ConversationKeyEnvelope[] }>(`/api/conversations/${conversationId}/keys`);
  const map = convKeys.get(conversationId) ?? new Map<number, CryptoKey>();
  for (const env of items) {
    if (map.has(env.version) || !env.senderId) continue;
    for (const signer of await trustedSigningKeys(env.senderId)) {
      try {
        map.set(env.version, await unwrapConversationKey(env.wrapped, { conversationId, version: env.version, recipientId: userId, senderId: env.senderId }, me, signer));
        break;
      } catch {
        /* try an older trusted key */
      }
    }
  }
  convKeys.set(conversationId, map);
}

/** A conversation key version, fetching and unwrapping on first use. Null if we weren't given it or it doesn't verify. */
export async function conversationKey(conversationId: string, version: number): Promise<CryptoKey | null> {
  if (convKeys.get(conversationId)?.has(version)) return convKeys.get(conversationId)!.get(version)!;
  let p = loading.get(conversationId);
  if (!p) {
    p = loadConversationKeys(conversationId).finally(() => loading.delete(conversationId));
    loading.set(conversationId, p);
  }
  await p;
  return convKeys.get(conversationId)?.get(version) ?? null;
}

/**
 * Make and distribute a new conversation key for every current member. Version
 * 1 turns encryption on. Refuses if anyone's key changed since we pinned it.
 */
export async function distributeNewKey(conv: Conversation): Promise<Conversation> {
  const { userId, identity: me } = requireReady();
  const ids = conv.members.map((m) => m.id);
  const keys = await publicKeys(ids, true);
  const missing = conv.members.filter((m) => !keys.has(m.id));
  if (missing.length) throw new Error(`${missing.map((m) => m.displayName).join(", ")} ${missing.length === 1 ? "hasn't" : "haven't"} set up encrypted messages yet`);
  const changed: string[] = [];
  for (const id of ids) if ((await trustOf(id, keys.get(id))) === "changed") changed.push(id);
  if (changed.length) throw new KeyChangedError(changed);
  const version = conv.encryption.keyVersion + 1;
  const fresh = await newConversationKey();
  const wrapped = await Promise.all(
    ids.map(async (id) => ({ userId: id, wrapped: await wrapConversationKey(fresh.raw, keys.get(id)!.encryptionKey, { conversationId: conv.id, version, recipientId: id, senderId: userId }, me) })),
  );
  fresh.raw.fill(0);
  const updated = await api.post<Conversation>(`/api/conversations/${conv.id}/keys`, { version, keys: wrapped });
  const map = convKeys.get(conv.id) ?? new Map<number, CryptoKey>();
  map.set(version, fresh.key);
  convKeys.set(conv.id, map);
  return updated;
}

export const currentIdentity = () => (state.status === "ready" ? identity : null);
